import { useState } from 'react';
import { PageHeader } from '../../components/ui/PageHeader/PageHeader';
import { Button } from '../../components/ui/Button/Button';
import { EmptyState } from '../../components/ui/EmptyState/EmptyState';
import { ConfirmModal } from '../../components/ui/ConfirmModal/ConfirmModal';
import { useToast } from '../../components/ui/Toast/ToastProvider';
import { useIsAdmin } from '../../hooks/useIsAdmin';
import {
    useCreateFaqMutation,
    useDeleteFaqMutation,
    useGetFaqsQuery,
    useUpdateFaqMutation,
} from '../../entities/faq/api/faqApi';
import type { FaqForm, FaqItem } from '../../entities/faq/model/types';
import { FaqModal } from '../../components/faq/FaqModal/FaqModal';
import styles from './FaqPage.module.scss';
import { Loading } from '../../components/ui/Loading/Loading';

export function FaqPage() {
    const toast = useToast();
    const isAdmin = useIsAdmin();

    const { data: items = [], isLoading, isError, refetch } = useGetFaqsQuery();
    const [createFaq, { isLoading: isCreating }] = useCreateFaqMutation();
    const [updateFaq, { isLoading: isUpdating }] = useUpdateFaqMutation();
    const [deleteFaq, { isLoading: isDeleting }] = useDeleteFaqMutation();

    const [pinned, setPinned] = useState<string | null>(null);

    const [editing, setEditing] = useState<FaqItem | null>(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [removing, setRemoving] = useState<FaqItem | null>(null);

    const openCreate = () => {
        setEditing(null);
        setModalOpen(true);
    };

    const openEdit = (item: FaqItem) => {
        setEditing(item);
        setModalOpen(true);
    };

    const togglePinned = (id: string) => setPinned((current) => (current === id ? null : id));

    const handleSubmit = async (form: FaqForm) => {
        try {
            if (editing) {
                await updateFaq({ id: editing.id, form }).unwrap();
                toast('Вопрос обновлён');
            } else {
                await createFaq(form).unwrap();
                toast('Вопрос добавлен');
            }
            setModalOpen(false);
        } catch {
            toast('Не удалось сохранить вопрос');
        }
    };

    const handleDelete = async () => {
        if (!removing) return;
        try {
            await deleteFaq(removing.id).unwrap();
            toast('Вопрос удалён');
            setRemoving(null);
        } catch {
            toast('Не удалось удалить вопрос');
        }
    };

    return (
        <div className={styles.page}>
            <PageHeader
                title="Часто задаваемые вопросы"
                actions={
                    isAdmin ? (
                        <Button variant="primary" onClick={openCreate}>
                            Добавить вопрос
                        </Button>
                    ) : undefined
                }
            />

            <div className={styles.pane}>
                {isError ? (
                    <div className={styles.state}>
                        <p>Не удалось загрузить вопросы.</p>
                        <Button onClick={() => refetch()}>Повторить</Button>
                    </div>
                ) : isLoading ? (
                    <Loading text="Загружаем вопросы…" size="md" />
                ) : items.length === 0 ? (
                    <EmptyState
                        title="Вопросов пока нет"
                        description="Добавьте первый вопрос, чтобы пользователи нашли ответ без обращения в поддержку."
                        action={isAdmin ? { label: 'Добавить вопрос', onClick: openCreate } : undefined}
                    />
                ) : (
                    <div className={styles.list}>
                        {items.map((item) => {
                            const isOpen = pinned === item.id;

                            return (
                                <article
                                    key={item.id}
                                    className={isOpen ? styles.itemOpen : styles.item}
                                >
                                    <div className={styles.head}>
                                        <button
                                            type="button"
                                            className={styles.summary}
                                            aria-expanded={isOpen}
                                            onClick={() => togglePinned(item.id)}
                                        >
                                            <span>{item.question}</span>
                                            <i className={styles.chevron} aria-hidden="true" />
                                        </button>

                                        {isAdmin && (
                                            <div className={styles.actions}>
                                                <button type="button" onClick={() => openEdit(item)}>
                                                    Изменить
                                                </button>
                                                <button
                                                    type="button"
                                                    className={styles.danger}
                                                    onClick={() => setRemoving(item)}
                                                >
                                                    Удалить
                                                </button>
                                            </div>
                                        )}
                                    </div>

                                    <div className={styles.body}>
                                        <div className={styles.bodyInner}>
                                            <p className={styles.answer}>{item.answer}</p>
                                            {item.images.map((src) => (
                                                <img key={src} className={styles.image} src={src} alt="" />
                                            ))}
                                        </div>
                                    </div>
                                </article>
                            );
                        })}
                    </div>
                )}
            </div>

            <ConfirmModal
                open={removing !== null}
                danger
                title={`Удалить вопрос «${removing?.question ?? ''}»?`}
                description="Вопрос пропадёт из раздела «Часто задаваемые вопросы» у всех пользователей. Отменить нельзя."
                confirmLabel="Удалить"
                busy={isDeleting}
                onConfirm={handleDelete}
                onClose={() => setRemoving(null)}
            />

            <FaqModal
                open={modalOpen}
                source={editing}
                saving={isCreating || isUpdating}
                onClose={() => setModalOpen(false)}
                onSubmit={handleSubmit}
            />
        </div>
    );
}